(function(){
  /**
   * Product Drawer Tabs — iOS-reliable tab switching
   *
   * Key fixes for iOS Safari:
   * 1. touch-action:manipulation on tab buttons (no 300ms tap delay)
   * 2. Tab state (aria-selected + panel visibility) set SYNCHRONOUSLY
   *    before the drawer transition starts, so the correct content is
   *    already in place when the drawer slides into view.
   * 3. Tab-strip scroll position deferred with requestAnimationFrame
   *    to run after MenuDrawer.resetScrollPosition (1ms timeout).
   * 4. click-only delegation (pointerup is unreliable inside iOS
   *    scroll-snap containers).
   */

  function selectTab(root, id){
    if(!root || !id) return;
    var idValue = id.startsWith('#') ? id.slice(1) : id;
    var list = root.querySelector('.tabs');
    var links = Array.from(list ? list.querySelectorAll('.tab__button') : []);
    var panels = Array.from(root.querySelectorAll('.drawer-tab-panel'));

    panels.forEach(function(p){ p.hidden = true; });
    links.forEach(function(a){ a.setAttribute('aria-selected','false'); });

    var target = panels.find(function(p){ return p.id === idValue; });
    if(target) target.hidden = false;

    var link = links.find(function(a){ return a.getAttribute('href') === '#' + idValue; });
    if(link){
      link.setAttribute('aria-selected','true');
    }
  }

  /**
   * Scroll the tab strip so the selected tab is visible.
   * Scheduled separately from selectTab so it can run after
   * MenuDrawer.resetScrollPosition's 1ms timeout and
   * SliderComponent.initPages.
   */
  function scrollTabIntoView(root, id, smooth){
    var idValue = id.startsWith('#') ? id.slice(1) : id;
    var list = root.querySelector('.tabs');
    if(!list) return;
    var links = Array.from(list.querySelectorAll('.tab__button'));
    var link = links.find(function(a){ return a.getAttribute('href') === '#' + idValue; });
    if(!link) return;
    var tabSlide = link.closest('.slider__slide');
    if(!tabSlide) return;
    var targetScrollLeft = tabSlide.offsetLeft;
    if(Math.abs(targetScrollLeft - list.scrollLeft) > 5){
      list.scrollTo({
        left: targetScrollLeft,
        behavior: smooth ? 'smooth' : 'instant'
      });
    }
  }

  /**
   * Apply touch-action:manipulation to all tab buttons inside drawers
   * so iOS doesn't wait 300ms for a double-tap-to-zoom check.
   */
  function applyTouchAction(){
    document.querySelectorAll('.menu-drawer__inner-container .tab__button').forEach(function(el){
      el.style.touchAction = 'manipulation';
    });
  }

  // --- Tab taps inside an already-open drawer ---

  // On iOS Safari, click events on <a> tags inside scroll-snap containers
  // within position:fixed transformed parents can be silently dropped.
  // We use touchend as the primary handler for touch devices, with click
  // as fallback for keyboard/mouse. A timestamp guard deduplicates when
  // both fire.
  //
  // IMPORTANT: we capture the target button at touchstart time using
  // e.target.closest(), NOT at touchend time via elementFromPoint().
  // elementFromPoint is unreliable inside scroll-snap containers on iOS
  // because the snap position can shift between touchstart and touchend,
  // causing elementFromPoint to return null or the wrong element.
  var lastTabTapTime = 0;
  var touchMovedDuringTap = false;
  var touchStartTarget = null;

  function handleTabTouchStart(e){
    touchMovedDuringTap = false;
    // Capture the tab button NOW — e.target is always accurate at
    // touchstart, before any scroll-snap repositioning can occur.
    var el = e.target;
    touchStartTarget = (el && el.closest) ? el.closest('a.tab__button') : null;
  }

  function handleTabTouchMove(){
    touchMovedDuringTap = true;
  }

  function handleTabTouchEnd(e){
    var a = touchStartTarget;
    touchStartTarget = null;
    if(touchMovedDuringTap) return; // was a scroll, not a tap
    if(!a) return;
    // Verify the button is still in the DOM (drawer might have closed)
    if(!a.isConnected) return;
    var container = a.closest('.menu-drawer__inner-container');
    if(!container) return;
    var href = a.getAttribute('href');
    if(!href || !href.startsWith('#')) return;

    e.preventDefault();
    lastTabTapTime = Date.now();
    selectTab(container, href);
    scrollTabIntoView(container, href, true);
  }

  function handleTabClick(e){
    var a = e.target.closest('a.tab__button');
    if(!a) return;
    var container = a.closest('.menu-drawer__inner-container');
    if(!container) return;
    var href = a.getAttribute('href');
    if(!href || !href.startsWith('#')) return;

    e.preventDefault();
    // Skip if touchend already handled this tap
    if(Date.now() - lastTabTapTime < 500) return;
    selectTab(container, href);
    scrollTabIntoView(container, href, true);
  }

  // --- Opening the drawer from a trigger button ---
  function handleDrawerTabOpen(e){
    var btn = e.target.closest('[data-open-tab]');
    if(!btn) return;
    var controls = btn.getAttribute('aria-controls');
    var tabId = btn.getAttribute('data-open-tab');
    if(!controls || !tabId) return;
    var drawer = document.getElementById(controls);
    if(!drawer) return;
    var container = drawer.querySelector('.menu-drawer__inner-container');
    if(!container) return;

    // Set tab state IMMEDIATELY (synchronous, before the CSS transition
    // begins) so the drawer slides into view with the right content.
    selectTab(container, tabId);
    applyTouchAction();

    // Defer the tab-strip scroll: MenuDrawer.resetScrollPosition runs
    // at 1ms and SliderComponent.initPages runs via ResizeObserver once
    // the drawer becomes visible. We schedule our scroll after both so
    // it isn't overwritten.
    // Use two rAFs to guarantee we run after layout + ResizeObserver.
    requestAnimationFrame(function(){
      requestAnimationFrame(function(){
        scrollTabIntoView(container, tabId, false);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function(){
    applyTouchAction();

    // touchend fires reliably on iOS even when click is swallowed
    document.addEventListener('touchstart', handleTabTouchStart, {passive: true});
    document.addEventListener('touchmove', handleTabTouchMove, {passive: true});
    document.addEventListener('touchend', handleTabTouchEnd);
    // click as fallback for mouse / keyboard
    document.addEventListener('click', handleTabClick);

    document.addEventListener('click', handleDrawerTabOpen);
  });
})();
